import { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  ROUTE_MANAGE,
  ROUTE_PAY_AND_POST_V2,
  ROUTE_SELECT_AD_TYPE,
  ROUTE_WRITE,
} from 'src/app/shared/constants/routes';
import { log } from 'src/app/utils/logging';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';
import type { AppState } from 'src/types/appState';

/**
 * A custom hook that redirects the user to the dashboard if the draftId is not set in the state.
 * This is useful for protecting routes that should not be accessed without a valid draftId.
 * Also address the bug of users clicking the browser back button multiple times after posting an ad
 *
 * @example
 * useRedirectIfDraftIdNotSetInState();
 *
 * @returns {void} Returns nothing. It performs a side effect of redirecting the user.
 */

export const useRedirectIfDraftIdNotSetInState = () => {
  const { draftId, queryParamsDraftId } = useSelector(
    (state: AppState) => ({
      draftId: state.session.draftId,
      queryParamsDraftId: state.app.queryParams.draftid,
    }),
    shallowEqual,
  );

  const { pathname } = useLocation();
  const { redirectToDashboard } = useLocalisedRoutes();

  useEffect(() => {
    const protectedRoutes = [
      ROUTE_SELECT_AD_TYPE,
      ROUTE_WRITE,
      ROUTE_MANAGE,
      ROUTE_PAY_AND_POST_V2,
    ].includes(pathname);

    const isDraftIdSet = queryParamsDraftId || draftId;
    // new draftId should be created after Classify page
    if (!isDraftIdSet && protectedRoutes) {
      log.error('useRedirectIfDraftIdNotSetInState | triggered', pathname);
      redirectToDashboard();
    }
  }, [draftId, pathname, redirectToDashboard, queryParamsDraftId]);
};
